import { useEffect, useState  } from "react";
import { useTranslation } from "react-i18next";
import {
    TEXT_FIELD_TYPE,
    NUMERIC_FIELD_TYPE,
    DATE_FIELD_TYPE
} from "../../helpers/constants";
import { IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FileUploadIcon from "@mui/icons-material/FileUpload";

import { useAuth } from "../../context/auth";
import { useDialogBetter } from "../../hooks/useDialogBetter";
import AddEditDocsRestrictedDialog from "../../components/dialog/AddEditDocsRestrictedDialog";
import { DOCS_RESTRICTED_ROLE, DOCS_RESTRICTED_USER } from "../../helpers/constants";
import { documentsApi } from "../../services/documentsService";
import { toast } from "react-hot-toast";
import { useAxiosPrivate } from "../../hooks/useAxiosPrivate";
import { axiosPrivate } from "../../api/axios";

import { DOC_SEGMENTS } from "../../schemas/documents";
import { useGenerateDocumentNumber } from "../../services/documentsService";
import { formatCurrency } from "../../components/common/PriceDisplay";

export const TRANSFER_TABLE_NAME = "transfers_table";


export const useTransferTableFieldsConfig = (props) => {
    const { t } = useTranslation();
    const { user } = useAuth()

    const axiosPrivate = useAxiosPrivate()
    const { openDialog, closeDialog } = useDialogBetter();
    

    const onDownloadFile = async (docId) => {
        try {
            await documentsApi.handleDownloadDocumentFromLink(axiosPrivate, docId);
        } catch (error) {
            toast.error(t("snackbar_alert.occurred_error_during_download"));
        }
    };

    const [transferDocsCurrentNumber, setTransferDocsCurrentNumber] = useState()
        
    const {data: docsNumberData} = useGenerateDocumentNumber(DOC_SEGMENTS.TRANSFER)

    useEffect(() => {
        if (docsNumberData && !transferDocsCurrentNumber) {
            setTransferDocsCurrentNumber(docsNumberData.document_number)
        }
    }, [docsNumberData]);

    const handleOpenDocsDialog = (idx, onAddDocs, onDeleteDocs, docsId, docsNote) => openDialog({
        Component: AddEditDocsRestrictedDialog,
        componentProps: {
            readOnly: props.isForInvoicingPage,
            request_user: user?.username || "",
            multiple: false,
            showIndustry: false,
            availableDocsRestrictedTypes: [
                DOCS_RESTRICTED_USER,
                DOCS_RESTRICTED_ROLE,
            ],
            docsId: docsId,
            docsNote: docsNote,
            docs_nr: transferDocsCurrentNumber,
            onSubmit: (docsIds, docs_note) => {
                // if create mode increase docs_nr and storage localy
                if (!docsId){
                    setTransferDocsCurrentNumber(prev=> {
                        const docsCounter = parseInt(prev.substring(0, 4))
                        return (docsCounter + 1).toString().padStart(4, "0") + prev.substring(4)
                    })
                }
                onAddDocs(idx, docsIds[0], docs_note)
                closeDialog();
            },
            showDownloadButton: docsId  ? true : false,
            canRemoveFiles: !props.isForInvoicingPage,
            returnDocsNoteOnSumbit: true,   
            onDownload: () => onDownloadFile(docsId),
            onDeleteFile: docsId ? () => {
                onDeleteDocs(idx);
                closeDialog();
            } : null
        },
    })

    return [
        {
            name: "transfer_agreement_nr",
            getValueCallback: (rowData) => rowData.transfer_agreement_nr,
            label: t("bankings.transfer_agreement_nr"),
            dataType: TEXT_FIELD_TYPE,
            editable: false,
        },
        {
            name: "agreement_customer",
            getValueCallback: (rowData) => rowData.agreement_customer,
            label: t("bankings.agreement_customer"),
            dataType: TEXT_FIELD_TYPE,
            editable: false,
        },
        {
            name: "agreement_company",
            getValueCallback: (rowData) => rowData.agreement_company,
            label: t("bankings.agreement_company"),
            dataType: TEXT_FIELD_TYPE,
            editable: false,
        },
        {
            name: "agreement_product",
            getValueCallback: (rowData) => rowData.agreement_product,
            label: t("bankings.agreement_product"),
            dataType: TEXT_FIELD_TYPE,
            editable: false,
        },
        {
            name: "transfer_amount",
            getValueCallback: (rowData) => rowData.transfer_amount,
            label: t("bankings.transfer_amount"),
            dataType: NUMERIC_FIELD_TYPE,
            editable: true,
        },
        {
            name: "transfer_date",
            getValueCallback: (rowData) => rowData.transfer_date,
            label: t("bankings.transfer_date"),
            dataType: DATE_FIELD_TYPE,
            editable: true,
        },
        {
            name: "remaining_total_price",
            getValueCallback: (rowData) => rowData.remaining_total_price,
            label: t("bankings.remaining_total_price"),
            dataType: TEXT_FIELD_TYPE,
            editable: false,
        },
        {
            name: "agrepay_due_date",
            getValueCallback: (rowData) => rowData.agrepay_due_date,
            label: t("bankings.agrepay_due_date"),
            dataType: TEXT_FIELD_TYPE,
            editable: false,
            getFilterFieldConfigCallback: (filteringData) => ({
                style: { minWidth: "100px" },
            }),
            sortable: true,
        },
        {
            name: "agrepay_total_price",
            getValueCallback: (rowData) => rowData.agrepay_total_price,
            label: t("bankings.agrepay_total_price"),
            filterType: TEXT_FIELD_TYPE,
            editable: false,
        },
        {
            name: "transfer_status",
            getValueCallback: (rowData) => rowData.transfer_status_name,
            label: t("bankings.transfer_status_name"),
            dataType: TEXT_FIELD_TYPE,
        },
        {
            name: "transfer_docs",
            label: t("bankings.transfer_docs"),
            dataType: null,
            getValueCallback: (rowData, idx, onDelete, disabledDelete, onAddDocs, onDeleteDocs) => (
                rowData.transfer_docs?.docs_id ? <IconButton
                    color={"primary"}
                    onClick={(e) => handleOpenDocsDialog(idx, onAddDocs, onDeleteDocs, rowData.transfer_docs.docs_id, rowData.transfer_docs.docs_note)}
                >
                    <EditIcon />
                </IconButton> :
                <IconButton
                    color={"primary"}
                    onClick={(e) => handleOpenDocsDialog(idx, onAddDocs)}
                    disabled={props.isForInvoicingPage}
                >
                    <FileUploadIcon />
                </IconButton>
            ),
        },
        {
            name: "delete",
            dataType: null,
            getValueCallback: (rowData, idx, onDelete, disabledDelete) => (
                <IconButton
                    color={"error"}
                    onClick={(e) => onDelete(idx)}
                    disabled={disabledDelete}
                >
                    <DeleteIcon />
                </IconButton>
            ),
        }
    ];
};
